import React from 'react';
import { get } from 'lodash';
import { SearchInput, useFormContext } from 'framework/form';
import { RedmineIssueDto } from './types';
import useTimeTrackingService from './useTimeTrackingService';
import { ListParams, ListResponse } from 'framework/list';

interface TicketInputProps {
  source: string;
  correctTicketIdSource?: string;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  disabled?: boolean;
}

const TicketInput: React.FC<TicketInputProps> = ({
  source,
  correctTicketIdSource,
  label = 'Ticket ID',
  placeholder = 'e.g. 975216',
  isRequired = false,
  disabled = false,
  ...props
}) => {
  const timeTrackingService = useTimeTrackingService();
  const { data = {}, onChangeCallback } = useFormContext();

  const searchRequest = async (params: ListParams): Promise<ListResponse<RedmineIssueDto>> => {
    const issueIdFilter = params.filter?.issue_id?.ct ? { eq: params.filter.issue_id.ct } : params.filter?.issue_id;

    const transformedParams: ListParams & { filter?: any } = {
      ...params,
      filter: {
        ...params.filter,
        ...(issueIdFilter ? { issue_id: issueIdFilter } : {}),
      },
    };

    try {
      return await timeTrackingService.searchIssues(transformedParams);
    } catch (error: any) {
      return {
        result: [],
        pageNumber: 0,
        totalPages: 0,
        totalElements: 0,
      };
    }
  };

  const mapResults = (issue: RedmineIssueDto) => {
    return {
      id: issue.id,
      text: `#${issue.id}: ${issue.subject || ''}`,
    };
  };

  const handleChange = (values: any) => {
    const ticketId = values[source];

    if (ticketId) {
      const updateData: any = {
        [source]: ticketId,
      };

      if (correctTicketIdSource) {
        updateData[correctTicketIdSource] = ticketId;
      }

      onChangeCallback?.(updateData);
    } else {
      onChangeCallback?.(values);
    }
  };

  const getInitialTicketLabel = async (): Promise<string> => {
    const ticketId = get(data, source);

    if (ticketId === undefined || ticketId === null || ticketId === '' || Number(ticketId) <= 0) {
      // Clear invalid backing value so the form cannot be submitted with an invalid ticket
      const clearData: any = {
        [source]: null,
      };

      if (correctTicketIdSource) {
        clearData[correctTicketIdSource] = null;
      }

      onChangeCallback?.(clearData);
      return '';
    }

    try {
      const issue = await timeTrackingService.getIssue(Number(ticketId));

      if (!issue) {
        // If issue is not found, clear backing value
        const clearData: any = {
          [source]: null,
        };

        if (correctTicketIdSource) {
          clearData[correctTicketIdSource] = null;
        }

        onChangeCallback?.(clearData);
        return '';
      }

      return `#${issue.id}: ${issue.subject || ''}`;
    } catch {
      // On error, also clear backing value
      const clearData: any = {
        [source]: null,
      };

      if (correctTicketIdSource) {
        clearData[correctTicketIdSource] = null;
      }

      onChangeCallback?.(clearData);
      return '';
    }
  };

  return (
    <SearchInput
      source={source}
      searchRequest={searchRequest}
      searchString="issue_id"
      mapResults={mapResults}
      noResultMessage="No tickets found"
      onChangeCallback={handleChange}
      customGetInitialValue={getInitialTicketLabel}
      {...props}
      // @ts-ignore - These props are passed through to AutosuggestDropdown
      label={label}
      placeholder={placeholder}
      isRequired={isRequired}
      disabled={disabled}
    />
  );
};

export default TicketInput;
